const INVALID_FILENAME_CHARS = /[<>:"/\\|?*]/g;

/** Class used so injected rules win over body’s light theme text color during html2canvas capture */
const PDF_EXPORT_ROOT_CLASS = "project-doc-pdf-export-root";

export function sanitizeDocumentFilename(title, fallback = "document") {
  let raw = String(title || "").trim() || fallback;
  raw = Array.from(raw)
    .map((ch) => (ch.charCodeAt(0) < 32 ? "_" : ch))
    .join("");
  const cleaned = raw.replace(INVALID_FILENAME_CHARS, "_").replace(/\s+/g, " ").trim().slice(0, 180);
  return cleaned || fallback;
}

export function downloadBlobAsFile(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function escapeHtmlAttr(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Builds a PDF from stored rich-text (Quill) HTML and triggers a browser download.
 */
export async function downloadWrittenProjectDocAsPdf({ title, projectName, html }) {
  const safeName = sanitizeDocumentFilename(title);
  const projectLine = projectName
    ? `<p style="font-size:12px;color:#4e5d78;margin:0 0 12px;font-family:system-ui,sans-serif;">Project: ${escapeHtmlAttr(
        projectName
      )}</p>`
    : "";

  const wrapper = document.createElement("div");
  wrapper.className = PDF_EXPORT_ROOT_CLASS;
  /* Inherit from body would be light-on-dark theme → invisible on white PDF; force print-safe colors */
  wrapper.style.setProperty("color", "#1f2b44");
  wrapper.style.setProperty("-webkit-text-fill-color", "#1f2b44");
  wrapper.style.position = "fixed";
  wrapper.style.left = "-9999px";
  wrapper.style.top = "0";
  wrapper.style.width = "794px";
  wrapper.style.boxSizing = "border-box";
  wrapper.style.padding = "24px";
  wrapper.style.background = "#ffffff";
  wrapper.style.pointerEvents = "none";
  wrapper.style.overflow = "visible";

  wrapper.innerHTML = `<style>
.${PDF_EXPORT_ROOT_CLASS}, .${PDF_EXPORT_ROOT_CLASS} * {
  color: #1f2b44 !important;
  -webkit-text-fill-color: #1f2b44 !important;
}
.${PDF_EXPORT_ROOT_CLASS} a {
  color: #2563eb !important;
  -webkit-text-fill-color: #2563eb !important;
}
.${PDF_EXPORT_ROOT_CLASS} .ql-editor { box-sizing: border-box; }
.${PDF_EXPORT_ROOT_CLASS} .ql-editor ol,
.${PDF_EXPORT_ROOT_CLASS} .ql-editor ul {
  padding-left: 1.5em;
  margin: 0.35em 0;
}
</style>${projectLine}<div class="ql-editor">${html || ""}</div>`;
  document.body.appendChild(wrapper);

  const [{ jsPDF }, html2canvasMod] = await Promise.all([import("jspdf"), import("html2canvas")]);
  const html2canvas = html2canvasMod.default || html2canvasMod;

  try {
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

    const canvas = await html2canvas(wrapper, {
      scale: Math.min(2, typeof window !== "undefined" ? window.devicePixelRatio || 1 : 2),
      backgroundColor: "#ffffff",
      useCORS: true,
      allowTaint: true,
      logging: false,
    });

    if (!canvas.width || !canvas.height) {
      throw new Error("Could not capture document layout for PDF.");
    }

    const imgData = canvas.toDataURL("image/jpeg", 0.92);
    const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait", compress: true });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 12;
    const contentW = pageW - 2 * margin;
    const contentH = pageH - 2 * margin;
    const imgW = contentW;
    const imgH = (canvas.height * imgW) / canvas.width;

    let heightLeft = imgH;
    let y = margin;
    doc.addImage(imgData, "JPEG", margin, y, imgW, imgH, undefined, "FAST");
    heightLeft -= contentH;

    while (heightLeft > 0) {
      y = margin - (imgH - heightLeft);
      doc.addPage();
      doc.addImage(imgData, "JPEG", margin, y, imgW, imgH, undefined, "FAST");
      heightLeft -= contentH;
    }

    doc.save(`${safeName}.pdf`);
  } finally {
    if (wrapper.parentNode) {
      wrapper.parentNode.removeChild(wrapper);
    }
  }
}
